// @ts-strict-ignore
import { Auth } from 'aws-amplify';
import { Formik } from 'formik';
import * as React from 'react';
import { Redirect, useHistory, useLocation } from 'react-router-dom';
import * as yup from 'yup';

import { ErrorText } from '@/atoms/ErrorText';
import { PASSWORD_POLICY_REGEX } from '@/components/AuthV2/common';
import { PasswordHelper } from '@/components/AuthV2/PasswordHelper';
import {
  AuthForm,
  FormFooter,
  InputsWrapper,
} from '@/components/AuthV2/wrappers';
import { Button } from '@/components/Button/Button';
import { FormikInputField } from '@/components/Input/Input';
import { PasswordField } from '@/components/PasswordField';
import { useResponsiveConstants } from '@/hooks/responsive/useResponsiveConstants';
import { OAUTH_ROUTES } from '@/nav-routes';
import { postLoginMessage } from '@/utils/broadcastMessages';
import { hasHTMLTags } from '@/utils/hasHTMLTags';

const validationSchema = yup.object({
  email: yup
    .string()
    .strict(true)
    .trim('Email contains white spaces, please remove them')
    .email('Please provide a valid email')
    .required('Please provide a valid email'),
  password: yup
    .string()
    .strict(true)
    .min(8, 'Password must be 8 characters long')
    .matches(
      PASSWORD_POLICY_REGEX,
      'Password does not match our password policy'
    )
    .required('Please provide a valid password'),
  name: yup
    .string()
    .strict(true)
    .trim('Name contains white spaces, please remove them')
    .required('Name is a required field')
    .test('should not contain html tags', 'Invalid User Name', (value) => {
      return !hasHTMLTags(value);
    }),
});

const errorMessageInvitedUsers = 'This account is not in the access list.';
const errorMessageIncorrectCredentials = 'Incorrect username or password.';
const errorMessageGeneric = 'Unknown error.';

export const SignupForm = function (): JSX.Element {
  const history = useHistory();
  const { search } = useLocation();

  const { mobile } = useResponsiveConstants();

  const searchParams = new URLSearchParams(search);
  const errorMessage = searchParams.get('error_description');

  const [error, setError] = React.useState<string | null>(() => {
    if (errorMessage) {
      if (errorMessage.match(/unique constraint/gi)) {
        return 'Please try to login instead.';
      } else if (errorMessage.match(/invalid user attributes/gi)) {
        return 'Please share name/email with us.';
      } else {
        return errorMessageGeneric;
      }
    }
    return null;
  });

  if (errorMessage?.match(/successfully linked/gi)) {
    return (
      <Redirect
        to={{ pathname: OAUTH_ROUTES.REDIRECT, state: { mode: 'sso-link' } }}
      />
    );
  }

  return (
    <Formik
      validateOnChange
      initialValues={{
        email: '',
        password: '',
        name: '',
      }}
      onSubmit={async (data, { setSubmitting }) => {
        setSubmitting(true);
        setError(null);
        try {
          const signUpResponse = await Auth.signUp({
            username: data.email.toLowerCase(),
            password: data.password.trim(),
            attributes: {
              name: data.name.trim(),
            },
          });

          if (signUpResponse && signUpResponse.user.getUsername()) {
            setSubmitting(false);

            history.replace(OAUTH_ROUTES.REDIRECT, {
              mode: 'awaiting-confirmation',
            });
          }

          postLoginMessage();
        } catch (e) {
          if (e && e.message) {
            if (e.message.match(/This account is not in the access list/gi)) {
              setError(errorMessageInvitedUsers);
            } else if (
              e.message.match(
                /An account with the given email already exists/gi
              )
            ) {
              setError(errorMessageIncorrectCredentials);
            } else if (e.message.match(/User is disabled/gi)) {
              setError('Something went wrong. Contact support@needl.ai');
            } else {
              setError(errorMessageGeneric);
            }
          } else {
            setError(errorMessageGeneric);
          }
          setSubmitting(false);
        }
      }}
      validationSchema={validationSchema}
    >
      {({ isSubmitting, isValid }) => (
        <AuthForm autoComplete='off'>
          <InputsWrapper>
            <FormikInputField
              placeholder='Name'
              name='name'
              type='text'
              label='Name'
              labelHidden
            />
            <FormikInputField
              placeholder='Email'
              name='email'
              type='text'
              label='Email'
              labelHidden
            />
            {/* @todo: Visit when refactoring base component  */}
            <PasswordField
              placeholder='Password'
              name='password'
              label='Password'
              labelHidden
            />
            <PasswordHelper />
          </InputsWrapper>
          <FormFooter>
            {error ? <ErrorText>{error}</ErrorText> : null}
            <Button
              loading={isSubmitting}
              disabled={!isValid || isSubmitting}
              fullWidth
              label='Register'
              size={mobile ? 'medium' : 'large'}
              variant='primary'
              submitButton
            />
          </FormFooter>
        </AuthForm>
      )}
    </Formik>
  );
};
